<template>
  <div class="bb-custom-form"
       :style="{
           gridGap: grid.gap,
           gridTemplateColumns: grid.columns,
           gridTemplateRows: grid.rows
         }">
    <div class="bb-custom-form-item"
         :style="input.style"
         v-for="(input, index) in cloneInputs"
         v-if="input && input.id && checkDisplayInput(input)"
         :key="input.id">
      <label class="bb-custom-form-item__label" :for="input.id">
        {{ getLabel(input) }}
      </label>
      <!-- string input -->
      <template v-if="input.type === 'string'">
        <input class="bb-input bb-input_border bb-custom-form-item__input"
               :class="{
                 'bb-custom-form-item__input_error': getError(input)
               }"
               @keyup.esc="close"
               @keyup.enter="save"
               v-autofocus="autofocus && index === 0"
               :value="input.group ? getGroupValue(input) : getFromModel(model, input)"
               :placeholder="input.placeholder"
               :autocomplete="input.autocomplete || 'off'"
               :id="input.id"
               @input="setValue($event, input)"
               type="text">
      </template>
      <!-- select input -->
      <template v-else-if="input.type === 'select'">
        <div class="bb-custom-form-item__select">
          <BBSelect :label="input.labelList"
                    :ref="input.id"
                    :type="input.selectType"
                    :error="getError(input)"
                    :model="input.model"
                    :placeholder="input.placeholder"
                    :searchable="input.searchable"
                    :options="input.list"
                    :orderBy="input.orderBy"
                    :clearable="input.clearable"
                    :value="input.group ? getGroupValue(input) : getValue(input)"
                    @keyup.esc="close"
                    @keyup.enter="save"
                    @dropdownOpen="changeSelectStatus"
                    @clickBySelected="clickBySelect"
                    @change="setSelect($event, input)"/>
          <transition name="bounce">
            <Popup class="bb-custom-form-item__popup"
                   @change="setPopupValue"
                   :style="{
                     position: popupCompanies.left ? 'fixed' : 'absolute',
                     left: popupCompanies.left + 10 + 'px',
                     top: popupCompanies.top + 30 + 'px'
                   }"
                   :radio="true"
                   :value="defaultModel.clientCompany ? defaultModel.clientCompany.id : ''"
                   :title="'Save For:'"
                   v-if="checkPopupCompany(input)"
                   :options="clientCompanies"
                   @close="setPopupValue"/>
          </transition>
        </div>
      </template>
      <!-- textarea input -->
      <template v-else-if="input.type === 'textarea'">
          <textarea class="bb-custom-form-item__textarea bb-input bb-input_border"
                    :class="{
                      'bb-custom-form-item__input_error': getError(input)
                    }"
                    :value="input.group ? getGroupValue(input) : getFromModel(model, input)"
                    @keyup.esc="close"
                    @input="setValue($event, input)"
                    :placeholder="input.placeholder"
                    autocomplete="off"/>
      </template>
      <!-- date input -->
      <template v-else-if="input.type === 'date'">
        <DataPicker @setDate="setDate"
                    :error="getError(input)"
                    :ref="input.group + input.counter"
                    :disabled="dateDisabled(model, input)"
                    :long="true"
                    :form="true"
                    :obj="input"
                    :no-input="input.group ? !getGroupValue(input) : !getFromModel(model, input)"
                    :default-date="input.group ? getGroupValue(input): getFromModel(model, input)"
                    :noDefaultDateStart="input.noDefaultDateStart"
        />
        <transition name="fade">
          <i class="icon-cross bb-custom-form-item__clear"
             v-if="displayHideClearData(model, input)"
             @click="setDate('', input)"/>
        </transition>
      </template>
      <!-- radio input -->
      <template v-else-if="input.type === 'radio'">
        <div class="bb-custom-form-item__radio">
          <radio v-for="radio in input.radio"
                 :name="input.model"
                 :key="radio.value"
                 :section="input"
                 :radio-text="radio.text"
                 :value="radio.value"
                 :checked="getValue(input) === radio.value || radio.checked"
                 @radioChange="radioChange"/>
        </div>
      </template>
      <!-- stamp input -->
      <template v-else-if="input.type === 'stamp'">
        <div class="bb-custom-form-item-stamp">
          <i class="icon-picture bb-custom-form-item-stamp__icon"
             v-if="getFromModel(model, input) && getFromModel(model, input, 'id')"
             :class="{
               'bb-custom-form-item-stamp__icon_disabled': fileIsDisabled(model, input)
             }"
             @click="openFile(getFromModel(model, input))"/>
          <span class="bb-custom-form-item-stamp-text__item_loading"
                v-else-if="getFromModel(model, input) && !getFromModel(model, input, 'id')">
            </span>
          <p class="bb-custom-form-item-stamp-text"
             :class="{
               'bb-custom-form-item-stamp__text_load': fileIsLoading(model, input)
             }">
            <template v-if="!getFromModel(model, input)">
              <input class="bb-custom-form-item-stamp-text__input"
                     :ref="input.id"
                     name="upload-logo"
                     title=""
                     type="file"
                     @click.stop
                     @change="processFile($event, input)">
              <span class="bb-custom-form-item-stamp-text__item">
                Upload {{ input.filename || 'file' }}
              </span>
            </template>
            <span class="bb-custom-form-item-stamp-text__item"
                  v-else-if="getFromModel(model, input, 'id')"
                  @click.stop="showControlFile = input.id">
              Edit {{ input.filename || 'file' }}
            </span>
            <span class="bb-custom-form-item-stamp-text__item"
                  v-else>
              Loading...
            </span>
          </p>
          <transition name="bounce">
            <Popup class="bb-custom-form-item__popup-file"
                   @change="setPopupValue($event, input)"
                   :radio="false"
                   title=""
                   v-if="showControlFile === input.id"
                   :options="filesControl"
                   @close="showControlFile = false"/>
          </transition>
        </div>
      </template>
      <!-- add/remove array values -->
      <template v-if="input.array">
        <div class="bb-custom-form-item-array-controls">
          <i class="icon-cross bb-custom-form-item-array-controls__icon bb-custom-form-item-array-controls__icon_add"
             v-if="displayAddInput(input)"
             @click="addItemInArray(input)"/>
          <i class="icon-minus bb-custom-form-item-array-controls__icon bb-custom-form-item-array-controls__icon_del"
             v-else
             @click="delItemInArray(input)"/>
        </div>
      </template>
      <!-- errors -->
      <transition name="fade">
        <span class="bb-custom-form-item__error"
              :style="input.styleError"
              v-if="getError(input)"
              ref="bbCustomFormError">
        {{ getError(input) }}
      </span>
      </transition>
    </div>
  </div>
</template>

<script>
import BBSelect from '@/components/Main/Inputs/BBSelect'
import Popup from '@/components/Main/Popup/Popup'
import DataPicker from '@/components/Main/DataPicker/DataPicker'
import radio from '@/components/Main/Inputs/radio'
import UploadFileApi from '@/api/UploadFile/api'
import setFileData from '@/mixin/setFileData'
import {get, set, cloneDeep, filter, findLastIndex, uniqueId, some, trimStart, groupBy, map} from 'lodash'
import b64toBlob from 'b64-to-blob'
import {saveAs} from 'file-saver'

export default {
  name: 'Form',
  components: {
    DataPicker,
    BBSelect,
    radio,
    Popup
  },
  props: {
    inputsList: {
      type: Array,
      default: () => []
    },
    defaultModel: {
      type: Object,
      default: () => {
        return {}
      }
    },
    errors: {
      type: Object,
      default: () => {
        return {}
      }
    },
    grid: {
      type: Object,
      default: () => {
        return {
          gap: '14px',
          columns: '180px 270px',
          rows: 'auto-fit, auto'
        }
      }
    },
    clientCompanies: {
      type: Array,
      default: () => []
    },
    close: {
      type: Function,
      default: () => {
      }
    },
    save: {
      type: Function,
      default: () => {
      }
    },
    target: {
      type: Object
    },
    autofocus: {
      type: Boolean,
      default: true
    }
  },
  mixins: [setFileData],
  data () {
    return {
      cloneInputs: [],
      selectOpen: false,
      showControlFile: false,
      showPopupCompany: false,
      model: {},
      groups: {},
      filesControl: [],
      popupCompanies: {},
      crypt: true,
      files: {}
    }
  },
  computed: {
    isFounder () {
      // checking permission to edit license without uploading a file
      // const userInfo = this.$store.getters.userInfo || {}
      return true // userInfo.role === FOUNDER.id
    }
  },
  methods: {
    getError (input) {
      return get(this.errors, input.model, '')
    },
    getFromModel (model, input, suffix) {
      const prop = suffix ? [input.model, suffix] : [input.model]
      if (input.group) {
        return get(this.groups, [input.group, input.counter, ...prop], '')
      } else {
        return get(this.model, prop.join('.'), '')
      }
    },
    fileIsLoading (model, input) {
      return this.getFromModel(model, input) && !this.getFromModel(model, input, 'id')
    },
    fileIsDisabled (model, input) {
      return !this.getFromModel(model, input) || !this.getFromModel(model, input, 'id')
    },
    checkDisplayInput (input) {
      if (input.hide) return false
      return input.display ? get(this.model, input.display, false) : true
    },
    getLabel (input) {
      const suffix = input.suffix || ''
      const numeration = input.numeration || ''
      const result = [input.label]
      if (numeration) result.push(numeration)
      if (suffix) result.push(suffix)
      return result.join(' ')
    },
    setDefaultFileControls () {
      this.filesControl = [
        {id: 1, name: 'Upload new', func: this.updateFile},
        {id: 2, name: 'Remove', func: this.deleteFile},
        {id: 3, name: 'Cancel', func: () => { this.showControlFile = false }}
      ]
    },
    uploadFile (fileData, input) {
      const file = this.setFileData(fileData)
      UploadFileApi.createFile(file, this.crypt)
        .then((resp) => {
          if (input.group) {
            set(this.groups, [input.group, input.counter, input.model], resp.data)
            this.groups = cloneDeep(this.groups)
          } else {
            set(this.model, input.model, resp.data)
          }
        })
        .catch((err) => {
          this.fileUploadError = true
          console.error(err)
        })
    },
    updateFile (input) {
      this.deleteFile(input)
      this.$nextTick(() => {
        const inputNode = this.$refs[input.id] || []
        if (inputNode[0]) inputNode[0].click()
      })
    },
    deleteFile (input) {
      if (input.group) {
        set(this.groups, [input.group, input.counter, input.model], '')
        this.groups = cloneDeep(this.groups)
      } else {
        set(this.model, input.model, '')
      }
      this.showControlFile = false
    },
    setNumbersInput (groupName) {
      const inputsGroup = filter(this.cloneInputs, {group: groupName})
      const groupsInput = groupBy(inputsGroup, 'counter')
      const groupsInputArray = map(groupsInput, item => item)
      groupsInputArray.forEach((groupArray, index) => {
        groupArray.forEach(input => {
          if (index > 0) input.numeration = index + 1
        })
      })
    },
    addItemInArray (input) {
      const groupName = input.group
      const counter = this.groups[groupName].length
      const filterParams = {group: groupName, counter: 0}
      const inputsGroup = filter(this.cloneInputs, filterParams)
      const insetIndex = findLastIndex(this.cloneInputs, {group: groupName})
      this.addGroup(inputsGroup, groupName, counter)
      this.addInputs(insetIndex, inputsGroup, counter)
      this.setNumbersInput(groupName)
    },
    delItemInArray (input) {
      const groupName = input.group
      const counter = input.counter
      const inputs = filter(this.cloneInputs, {group: groupName, counter: counter})
      inputs.forEach(item => {
        const index = findLastIndex(this.cloneInputs, {id: item.id})
        this.cloneInputs.splice(index, 1, {})
      })
      this.groups[groupName].splice(counter, 1, {})
      this.groups = cloneDeep(this.groups)
      this.setNumbersInput(groupName)
    },
    addGroup (inputs, groupName, counter) {
      const modelGroup = {}
      inputs.map(input => {
        modelGroup[input.model] = get(this.model, [input.group, counter, input.model], '')
      })
      this.groups[groupName].push(modelGroup)
    },
    addInputs (index, inputs, counter) {
      const createInputs = counter ? cloneDeep(inputs) : inputs
      const newInputs = createInputs.map(item => {
        item.counter = counter
        item.id += uniqueId(counter)
        return item
      })
      if (counter) {
        this.cloneInputs.splice(index + 1, 0, ...newInputs)
      }
    },
    displayAddInput (input) {
      return !input.counter
    },
    processFile (e, input) {
      const file = e.target.files[0]
      if (!file) return
      if (input.group) {
        set(this.groups, [input.group, input.counter, input.model], file)
        this.groups = cloneDeep(this.groups)
      } else {
        set(this.model, input.model, file)
      }
      this.uploadFile(file, input)
    },
    openFile (file) {
      this.files[file.id] ? saveAs(this.files[file.id].blob, this.files[file.id].label) : this.getFile(file)
    },
    getFile (file) {
      this.$emit('loader', true)
      UploadFileApi.getFile(file.id, this.crypt)
        .then((resp) => {
          const blob = b64toBlob(resp.data.base64, resp.data.type)
          this.files[resp.data.id] = {id: resp.data.id, blob, label: resp.data.label}
          saveAs(blob, resp.data.label)
          this.$emit('loader', false)
        })
        .catch((err) => {
          console.error(err)
          this.$emit('loader', false)
        })
    },
    radioChange (value, input) {
      if (input.group) {
        set(this.groups, [input.group, input.counter, input.model], value)
        this.groups = cloneDeep(this.groups)
      } else {
        set(this.model, input.model, value)
      }
    },
    setValue (e, input) {
      const value = e.target.value
      let valueFormat = trimStart(value)
      if (input.format === 'number' && typeof valueFormat === 'string' && valueFormat) {
        valueFormat = +valueFormat.replace(/[^.\d]/gmi, '')
      }
      if (input.group) {
        set(this.groups, [input.group, input.counter, input.model], valueFormat)
        this.groups = cloneDeep(this.groups)
      } else {
        set(this.model, input.model, valueFormat)
      }
      e.target.value = valueFormat
    },
    getGroupValue (input) {
      return get(this.groups, [input.group, input.counter, input.model], '')
    },
    displayHideClearData (model, input) {
      const hasValue = !input.require && input.group ? this.getGroupValue(input) : this.getFromModel(model, input)
      const disabledPicker = this.dateDisabled(model, input)
      return !disabledPicker && hasValue
    },
    dateDisabled (model, input) {
      if (input.group) {
        const prop = get(input, 'editableDate.prop', '')
        const equalProp = get(input, 'editableDate.equal', '')
        const val = get(this.groups, [input.group, input.counter, prop], '')
        return val !== equalProp && val && !this.isFounder
      } else {
        const prop = get(input, 'editableDate.prop', '')
        const equalProp = get(input, 'editableDate.equal', '')
        const val = get(this.model, prop, '')
        return val !== equalProp && val && !this.isFounder
      }
    },
    setDate (date, input, info) {
      const defaultDate = get(info, 'defaultDate', '')
      const noDefaultDate = get(info, 'noDefaultDate', '')
      const setDate = defaultDate || !noDefaultDate ? date : ''
      if (input.group) {
        set(this.groups, [input.group, input.counter, input.model], setDate)
        this.groups = cloneDeep(this.groups)
      } else {
        set(this.model, input.model, setDate)
      }
    },
    setPopupValue (value, input) {
      if (value && value.func) {
        value.func(input)
      } else {
        this.$emit('setPopupValue', value)
      }
      this.showPopupCompany = false
    },
    setDefaultValues () {
      this.setDefaultFileControls()
      const object = {}
      this.cloneInputs = cloneDeep(this.inputsList)
      this.inputsList.forEach(input => {
        // set groups
        const groupName = input.group
        const createGroup = groupName && !this.groups.hasOwnProperty(groupName)
        if (createGroup) {
          this.model[input.group] = cloneDeep(this.defaultModel[input.group])
          object[input.group] = cloneDeep(this.defaultModel[input.group])
          set(this.groups, groupName, [])
          const groupValue = get(object, groupName, [''])
          if (groupValue.length === 0) groupValue.push('')
          groupValue.forEach(groupObj => {
            this.addItemInArray(input)
          })
        } else if (!groupName) {
          const value = cloneDeep(get(this.defaultModel, input.model, ''))
          set(this.model, input.model, value)
          set(object, input.model, this.getValue(input))
        }
      })
      this.model = object
    },
    getValue (input) {
      const aloneValue = get(this.model, input.model, '')
      const groupValue = get(this.model, [input.group, input.counter, input.model], '')
      const value = input.group ? groupValue : aloneValue
      const defaultValue = input.default || ''
      const clearableValue = input.clearable ? '' : defaultValue
      return value === '' ? clearableValue : value
    },
    setSelect (data, input) {
      if (input.group) {
        set(this.groups, [input.group, input.counter, input.model], data)
        this.groups = cloneDeep(this.groups)
      } else {
        set(this.model, input.model, data)
      }
      const select = get(this.$refs, [input.id, 0, '$el'], '')
      if (select && input.id === 'own') {
        this.popupCompanies = select.getBoundingClientRect() || {}
      }
      if (input.selectPopup && data.popup) {
        setTimeout(() => {
          this.showPopupCompany = true
        }, 200)
      }
    },
    clickBySelect (val) {
      if (val && val.popup) {
        this.showPopupCompany = !this.showPopupCompany
      }
    },
    changeSelectStatus (value) {
      this.selectOpen = value
    },
    checkPopupCompany (input) {
      const hasPopup = input.selectPopup && (this.getValue(input) ? this.getValue(input).popup : false)
      const hasCompanies = this.clientCompanies.length > 1
      if (this.clientCompanies.length === 1) {
        this.setPopupValue(this.clientCompanies[0], input)
      }
      return !this.selectOpen && hasPopup && this.showPopupCompany && hasCompanies
    },
    findErrorNode () {
      const errorNodes = this.$refs.bbCustomFormError || []
      this.$emit('error', errorNodes[0], this.target)
    }
  },
  watch: {
    model: {
      deep: true,
      handler (newVal) {
        const valueForm = Object.assign(newVal, this.groups)
        this.$emit('change', valueForm, this.target)
      }
    },
    groups (newVal) {
      const valueForm = Object.assign(newVal, this.groups)
      this.$emit('change', valueForm, this.target)
    },
    errors (errors) {
      const hasError = some(errors, item => item !== false)
      if (hasError) {
        this.$nextTick(this.findErrorNode)
      }
    }
  },
  beforeMount () {
    this.setDefaultValues()
  }
}
</script>

<style lang="scss" scoped>
@import "../../../sass/variables";
@import "../../../sass/mixins";

.bb-custom-form {
  @include global-font;
  display: grid;
  grid-template-columns: repeat(auto-fit, auto);
  grid-template-rows: repeat(auto-fit, auto);
  grid-gap: 18px;

  &-item {
    position: relative;
    display: grid;
    font-weight: 300;
    font-size: 13px;
    line-height: 16px;

    &__label {
      font-weight: 600;
      font-size: 13px;
      line-height: 16px;
      color: $color-accent;
    }

    &__popup {
      position: relative;
    }

    &__error {
      position: absolute;
      right: 0;
      color: $color-red-orange;
      font-size: 11px;
      line-height: 13px;
      background-color: $color-white;
      pointer-events: none;
    }

    &__input {
      position: relative;
      font-size: 13px;
      min-height: 30px;
      transition: 0.3s;
      border-color: $color-mercury;

      &_error {
        border-color: $color-red-orange;
      }

      &:focus {

      }
    }

    &__select {
      font-size: 13px;
      position: relative;
      height: 30px;
    }

    &__textarea {
      font-size: 13px;
      resize: none;
      min-height: 73px;
    }

    &__radio {
      display: flex;
    }

    &__clear {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border-radius: 5px;
      border: 1px solid $color-wild-sand;
      color: $color-accent;
      cursor: pointer;
      transition: 0.3s;

      &:hover {
        color: $color-accent-hover;
      }
    }

    &-array-controls {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      color: $color-accent;
      border: 1px solid $color-wild-sand;
      box-sizing: border-box;
      border-radius: 5px;
      cursor: pointer;
      transition: 0.3s;

      &:hover {
        color: $color-accent-hover;
      }

      &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;

        &_del {
          font-size: 2px;
        }

        &_add {
          font-size: 10px;
          transform: rotate(45deg);
        }
      }
    }

    &__popup-file {
      left: 40px;
      top: 30px;
    }

    &-stamp {
      display: flex;
      align-items: center;
      position: relative;

      &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 33px;
        min-height: 33px;
        border: 1px dashed $color-mercury;
        box-sizing: border-box;
        border-radius: 5px;
        color: $color-accent;
        font-size: 15px;
        transition: 0.3s;
        cursor: pointer;
        margin-right: 8px;

        &_disabled {
          pointer-events: none;
          color: $color-silver-chalice;
        }
      }

      &-text {
        display: flex;
        align-items: center;
        width: 176px;
        height: 33px;
        position: relative;
        font-weight: 500;
        font-size: 12px;
        line-height: 33px;
        color: $color-silver-chalice;
        transition: 0.3s;
        cursor: pointer;

        &__item {
          display: block;
          position: absolute;
          left: 0;
          top: auto;

          &_loading {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 33px;
            min-height: 33px;
            border: 1px dashed $color-mercury;
            box-sizing: border-box;
            border-radius: 5px;
            color: $color-accent;
            margin-right: 8px;

            &:before {
              content: '';
              position: absolute;
              width: 0;
              left: 9px;
              top: auto;
              height: 3px;
              background-color: $color-accent;
              border-radius: 1px;
              transition: .3s;
              animation: loader 2s linear infinite;
            }

            @keyframes loader {
              from {
                width: 0;
              }

              to {
                width: 15px;
              }
            }
          }
        }

        &__input {
          width: 0;
          height: 100%;
          cursor: pointer;
          padding-left: 100px;
          overflow: hidden;
          opacity: 0;
          z-index: 1;

          &:hover + i {
            color: $color-accent-hover;
          }
        }

        &_load {
          pointer-events: none;
        }

        &:hover {
          color: $color-accent;
        }
      }
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Main/Form/Form.vue